import { Capacitor } from '@capacitor/core'
import { FirebaseAuthentication } from '@capacitor-firebase/authentication'
import type { User } from 'firebase/auth'
import { httpsCallable } from 'firebase/functions'
import { functions } from './firebase'
import { initializePlatformServices } from './platform'
import { runNativeAuthCall } from '../utils/nativeFirebaseAuthQueue'
import { syncNativeAuthToWeb } from '../utils/nativeAuth'

export const GUEST_WORKSPACE_ADOPTED_EVENT = 'voxnela:guest-workspace-adopted'

const callRegisterGuestDevice = httpsCallable<
  { platform: 'ios' | 'android'; deviceId: string },
  { ok: boolean; guest: boolean; freeCreditsEligible: boolean }
>(functions, 'registerGuestDevice')

const callAdoptGuestWorkspace = httpsCallable<
  { guestIdToken: string },
  { ok: boolean; movedProjects: number; restorePurchases: boolean }
>(functions, 'adoptGuestWorkspace')

// The App Store build lets people create and subscribe without registering
// (Guideline 5.1.1(v)). The website keeps its account-based flow.
export function guestSessionsSupported(): boolean {
  return Capacitor.isNativePlatform()
}

export async function isGuestUser(user: User | null): Promise<boolean> {
  if (!user) return false
  if (user.isAnonymous) return true
  const { claims } = await user.getIdTokenResult()
  return claims.guest === true
}

/**
 * Starts the anonymous session on the native layer so it is kept in the
 * Keychain, then mirrors it into the Web SDK like every other native session.
 */
export async function startNativeGuestSession(): Promise<User> {
  await initializePlatformServices()
  await runNativeAuthCall('signInAnonymously', () => FirebaseAuthentication.signInAnonymously())
  return syncNativeAuthToWeb()
}

export async function registerGuestDevice(): Promise<void> {
  const platform = Capacitor.getPlatform()
  if (platform !== 'ios' && platform !== 'android') return
  const { Device } = await import('@capacitor/device')
  const { identifier } = await Device.getId()
  await initializePlatformServices()
  await callRegisterGuestDevice({ platform, deviceId: identifier })
}

/**
 * Moves the guest's images into the account that was just signed in to and,
 * when the guest had bought a subscription, restores it onto that account.
 */
export async function adoptGuestWorkspace(guestIdToken: string, userId: string): Promise<void> {
  await initializePlatformServices()
  const { data } = await callAdoptGuestWorkspace({ guestIdToken })
  if (data.restorePurchases && Capacitor.isNativePlatform()) {
    const { freeMembershipProfile, restoreNativeMembershipProfile } = await import('./membership')
    await restoreNativeMembershipProfile(userId, freeMembershipProfile()).catch((error) => {
      console.warn('[VOXNELA_GUEST] Misafir aboneliği hesaba geri yüklenemedi.', error)
    })
  }
  window.dispatchEvent(new CustomEvent(GUEST_WORKSPACE_ADOPTED_EVENT, { detail: data }))
}
